import React, { Dispatch, MouseEvent, useRef, useState, useEffect } from 'react';
import { DebouncedFunc } from 'lodash';
import '../App.css';
import { useDrag, useDrop, DragSourceMonitor } from 'react-dnd'
import { ItemTypes } from '../ItemTypes'
import { IInject, IRFI, ISelectedOption, IScenario } from '../interfaces';
import { SelectedOption } from '../Classes1';
import type { Identifier, XYCoord } from 'dnd-core'
import ContentEditable from 'react-contenteditable'
import Select, { components, StylesConfig } from 'react-select'

type OptionType = {
  value: string;
  label: string;
};

interface RFIProps {
    index: number;
    nodeId: string;
    moveVote: (dragIndex: number, hoverIndex: number) => void
    setInjectItem: Dispatch<any>;
    injectItem: IInject;
    isRFIEditing: string;
    injectComponents: IInject[];
    setInjectComponents: Dispatch<any>;
    rfi: IRFI;
    rfiOpen: string;
    selectedScenario: IScenario;
    onChangeSave: DebouncedFunc<any>;
    onFocus: () => void;
    onBlur: () => void;
  }

  interface DragItem {
    index: number
    id: string
    type: string
  }

const RFIVote: React.FC<RFIProps> = ({
  index, rfi, nodeId, injectItem, injectComponents, selectedScenario,
  isRFIEditing, rfiOpen,
  setInjectItem, setInjectComponents, onChangeSave,
  moveVote, onFocus, onBlur,
}) => {

  let [selectedOption, setSelectedOption] = useState<ISelectedOption>(new SelectedOption({}))

  const contentEditableRef = useRef<any>()

  const options: ISelectedOption[] = selectedScenario.roles.map((r) => ({
    label: r, value: r,
  }))
  options.push({ label: 'All', value: 'All' });

  const injectItemRef = useRef<IInject>()
  useEffect(() => {
    injectItemRef.current = injectItem
  });

  useEffect(() => {
    const currentRole = rfi.roleContent[0]?.role || 'All';
    setSelectedOption({ label: currentRole, value: currentRole  })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps

  const selectedOptionRef = useRef<any>()
  useEffect(() => {
    selectedOptionRef.current = selectedOption
  });

  const updateRfi = (updatedRfi: IRFI) => {
    if (!injectItemRef.current) { return; }
    const nodesUpdate = injectItemRef.current.nodes.map((item) => {
      if (item.id !== nodeId) {
        return item; // No change
      }

      const rfis = item.rfis.slice();
      rfis.splice(index, 1, updatedRfi);

      return {
        ...item,
        rfis,
      }
    })

    setInjectItem({
      ...injectItem,
      nodes: nodesUpdate
    });
  }

  const onChangeRole = (role: string) => {
    if (role === selectedOptionRef.current.value) {
      return;
    }

    setTimeout(() => {
      onChangeSave();
      onChangeSave.flush();
    }, 500)

    setSelectedOption(new SelectedOption({label: role, value: role}))

    const updatedRfi: IRFI = JSON.parse(JSON.stringify(rfi));
    updatedRfi.roleContent[0].role = role;

    updateRfi(updatedRfi);
  }

  const onChangeAnswer = (ev: React.FormEvent<HTMLInputElement>) => {
    const updatedRfi: IRFI = JSON.parse(JSON.stringify(rfi));
    updatedRfi.roleContent[0].displayText = ev.currentTarget.innerText;
    updateRfi(updatedRfi);
    onChangeSave();
  }

  const onChangeVotes = (ev: React.FormEvent<HTMLInputElement>) => {
    const updatedRfi: IRFI = JSON.parse(JSON.stringify(rfi));
    updatedRfi.displayText = ev.currentTarget.innerText;
    updateRfi(updatedRfi);
    onChangeSave();
  }

  const trashVote = (ev: MouseEvent) => {
    ev.stopPropagation();
    const updatedNodes = injectItem.nodes.map((item) => {
      if (item.id !== nodeId) {
        return item;
      }

      const rfis = item.rfis.filter((meh, localIdx) => localIdx !== index);
      return {
        ...item,
        rfis
      }
    })

    setInjectItem({
      ...injectItem,
      nodes: updatedNodes
    });

    setInjectComponents(injectComponents.map((inject: IInject) => {
      if (inject.id === injectItem.id) {
          return { ...inject, nodes: updatedNodes };
      } else {
          return inject;
      }
    }));
    onChangeSave();
    onChangeSave.flush();
  }

  const ref = useRef<HTMLDivElement>(null)
  const [{ handlerId }, drop] = useDrop<
    DragItem,
    void,
    { handlerId: Identifier | null }
  >({
    accept: ItemTypes.VOTE,
    collect(monitor) {
      return {
        handlerId: monitor.getHandlerId(),
      }
    },
    hover(item: DragItem, monitor) {
      if (!ref.current) {
        return
      }
      
      const dragIndex = item.index
      const hoverIndex = index

      if (dragIndex === hoverIndex) {
        return
      }

      const hoverBoundingRect = ref.current?.getBoundingClientRect()

      const hoverMiddleY =
        (hoverBoundingRect.bottom - hoverBoundingRect.top) / 2

      const clientOffset = monitor.getClientOffset()

      const hoverClientY = (clientOffset as XYCoord).y - hoverBoundingRect.top

      if (dragIndex < hoverIndex && hoverClientY < hoverMiddleY) {
        return
      }

      if (dragIndex > hoverIndex && hoverClientY > hoverMiddleY) {
        return
      }

      moveVote(dragIndex, hoverIndex)

      item.index = hoverIndex
    },
  })

  const [{ isDragging }, drag] = useDrag({
    type: ItemTypes.VOTE,
    item: () => {
      return { id: rfi.id, index }
    },
    collect: (monitor: DragSourceMonitor) => ({
      isDragging: monitor.isDragging(),
    }),
  })

  const opacity = isDragging ? 0 : 1
  drag(drop(ref))

  const CustomDropdownIndicator = (props: any) => {
    return (
      <components.DropdownIndicator {...props}>
        <span className="arrow"/>
      </components.DropdownIndicator>
    );
  };

  return <div ref={ref} style={{ opacity }} className={`rfi-vote ${isRFIEditing} ${rfiOpen === 'closed' ? 'rfi-hidden' : ''}`}
    data-voteid={rfi.id} data-handler-id={handlerId}>
      <div className="vote-number">{index + 1}</div>
      <ContentEditable
          className={`rfi-vote-question ${isRFIEditing}`}
          data-placeholder="RFI Question text here..."
          ref={contentEditableRef}
          data-nodeid={nodeId}
          html={rfi.displayText} // innerHTML of the editable div
          disabled={false}       // use true to disable editing
          onChange={onChangeVotes} // handle innerHTML change
          onFocus={onFocus}
          onBlur={onBlur}
      />
      <button className={`vote-trash ${isRFIEditing}`} onClick={trashVote} data-voteid={rfi.id}>
          <img src="img/trash-can.svg" alt="trash can" className="vote-trash-svg" />
      </button>
      <ContentEditable
          className="rfi-vote-desc"
          data-placeholder="RFI Answer text here..."
          ref={contentEditableRef}
          data-nodeid={nodeId}
          html={rfi.roleContent[0].displayText} // innerHTML of the editable div
          disabled={false}       // use true to disable editing
          onChange={onChangeAnswer} // handle innerHTML change
          onFocus={onFocus}
          onBlur={onBlur}
      />
      <Select
          value={selectedOption}
          components={{ DropdownIndicator: CustomDropdownIndicator }}
          onChange={(val) => onChangeRole(val?.value || 'All')}
          options={options}
          styles={dropdownStyles}
          className={'rfi-participants-styles custom-select'}
      />
  </div>
}

const dropdownStyles: StylesConfig<OptionType, false> = {
  option: (provided, { isFocused } : { isFocused: boolean, isSelected: boolean }) => ({
    ...provided,
    backgroundColor: isFocused ? "#393939" : "#191B22",
    fontSize: 13
  }),
  singleValue: (provided) => ({
      ...provided,
      padding: 0,
      color: '#AAADBC',
      backgroundColor: "#191B22",
      width: 55,
      fontSize: 13
  }),
  container:  (provided) => ({
    ...provided,
    backgroundColor: "#191B22",
    border: "none",
    borderBottom: "1px solid #383C4D",
    width: 55,
    height: 24,
  }),
  control:  (provided) => ({
    ...provided,
    backgroundColor: "#191B22",
    color: "#AAADBC",
    borderRadius: 0,
    border: "none",
    width: 55,
    height: 20,
    minHeight: 24
  }),
  placeholder:  (provided) => ({
    ...provided,
    color: "#AAADBC",
    fontSize: 13
  }),
  indicatorSeparator:  (provided) => ({
    ...provided,
    display: "none"
  }),
  menu:  (provided) => ({
    ...provided,
    width: 55,
    color: '#AAADBC',
    backgroundColor: "#191B22",
    top: 20,
    zIndex: 1400,
  }),
  menuList:  (provided) => ({
    ...provided,
    color: '#AAADBC',
    backgroundColor: "#191B22",
    paddingTop: 0,
    paddingBottom: 0,
    zIndex: 140
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    color: '#191B22',
    padding: 0,
    height: 24
  }),
  input:  (provided) => ({
      ...provided,
      height: 24,
      marginTop: -6
    })
}

export default RFIVote;